import { IEventDefinition } from "@highway/mbs-analytics";

export const globalEvents: Record<string, IEventDefinition> = {
    // Global events
    page_view: {
        description: "Fires when a user lands on page.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_page_view`,
            pageAnalytics: "page_view",
        },
    },
    recording_settings_update: {
        description: "Fires when a user updates their recording settings.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_recording_settings_update`,
        },
        validTools: ["sst"],
    },
    share_action: {
        description: "Fires when a user clicks the share option.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_share_action`,
        },
    },
    share_success: {
        description: "Fires when a user successfully shares.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_share_success`,
        },
    },
    ad_widget_impression: {
        description: "Fires when the ad widget is displayed on the page.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_ad_widget_impression`,
        },
    },
    ad_widget_click: {
        description: "Fires when a user clicks on an ad within the page.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_ad_widget_click`,
        },
    },
    video_start: {
        description: "Fires when the user clicks play (either on the thumbnail or the play button).",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_video_start`,
        },
        validTools: ["bi", "mu", "woh"],
    },
    video_stop: {
        description: "Fires when the user stops the video manually or navigates away.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_video_stop`,
        },
        validTools: ["bi", "mu", "woh"],
    },
    video_finish: {
        description: "Fires when a user watches the video to completion.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_video_finish`,
        },
        validTools: ["bi", "mu", "woh"],
    },
    create_new_click: {
        description: "Fires when the user clicks 'Create New'.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_create_new_click`,
        },
    },
    reset_click: {
        description: "Fires when the user clicks 'Reset Calculator'.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_reset_click`,
        },
    },
    reset_confirm: {
        description: "Fires when the user confirms a calculator reset.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_reset_confirm`,
        },
    },
    mode_change: {
        description: "Fires when the user changes calculator mode. Properties: mode.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_mode_change`,
        },
        validTools: ["aff"],
    },
    review_to_edit_click: {
        description: "Fires when the user clicks 'Edit' from the Review Report view.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_review_to_edit_click`,
        },
    },
    edit_to_review_click: {
        description: "Fires when the user clicks 'Review Report' and views the report in preview mode.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_edit_to_review_click`,
        },
    },
    edit_report_view: {
        description: "Fires when the user lands on the Edit Report view after submitting info.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_edit_report_view`,
        },
    },
    review_report_view: {
        description: "Fires when the user lands on the Review Report view.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_review_report_view`,
        },
    },
    report_view: {
        description: "Fires when the user views the generated report.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_report_view`,
        },
    },
    lock_alert_banner_viewed: {
        description: "Fires when lock alert is displayed to user.",
        mappings: {
            mixpanel: "lock_alert_banner_viewed",
        },
    },
    lock_alert_banner_closed: {
        description: "Fires when lock alert is closed.",
        mappings: {
            mixpanel: "lock_alert_banner_closed",
        },
    },
    lock_alert_banner_sound_started: {
        description: "Fires when lock alert audio begins playing.",
        mappings: {
            mixpanel: "lock_alert_banner_sound_started",
        },
    },
    lock_alert_banner_sound_stop: {
        description: "Fires when lock alert audio is stopped before completion.",
        mappings: {
            mixpanel: "lock_alert_banner_sound_stop",
        },
    },
    lock_alert_banner_sound_finish: {
        description: "Fires when lock alert audio plays until the end.",
        mappings: {
            mixpanel: "lock_alert_banner_sound_finish",
        },
    },
    share_clicked: {
        description: "Fires when the user click the Share button.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_share_clicked`,
        },
    },
    location_entered: {
        description: "Fires when a user enters a location.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_location_entered`,
        },
    },
    live_link_viewed: {
        description: "Fires when a user views the live link.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_live_link_viewed`,
        },
    },
    recording_action: {
        description: "Fires when a user interacts with the recording session.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_recording_action`,
        },
        validTools: ["record"],
    },
    send_modal_view: {
        description: "Fires when the send modal opens.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_send_modal_view`,
        },
        validTools: ["record"],
    },
    chatbot_fab_click: {
        description: "Fires when the user clicks the floating action button (FAB) to open ChatBot.",
        mappings: {
            mixpanel: "chatbot_fab_click",
        },
    },
    chatbot_first_interaction: {
        description:
            "Fires when the first user message (voice or text) is submitted in an interaction period.",
        mappings: {
            mixpanel: "chatbot_first_interaction",
        },
    },
    chatbot_interaction_end: {
        description: "Fires when an interaction period ends (10 min inactivity or user exits).",
        mappings: {
            mixpanel: "chatbot_interaction_end",
        },
    },
    chatbot_voice_input_click: {
        description: "Fires when the user clicks the mic button to start voice input.",
        mappings: {
            mixpanel: "chatbot_voice_input_click",
        },
    },
    chatbot_voice_input_submitted: {
        description: "Fires when the user submits a message via voice.",
        mappings: {
            mixpanel: "chatbot_voice_input_submitted",
        },
    },
    chatbot_text_input_submitted: {
        description: "Fires when the user submits a message via text.",
        mappings: {
            mixpanel: "chatbot_text_input_submitted",
        },
    },
    chatbot_voice_input_error: {
        description: "Fires when voice input fails.",
        mappings: {
            mixpanel: "chatbot_voice_input_error",
        },
    },
    chatbot_error: {
        description: "General error during ChatBot usage.",
        mappings: {
            mixpanel: "chatbot_error",
        },
    },
    chatbot_feedback_submitted: {
        description: "Fires when user clicks thumbs up/down and submits feedback.",
        mappings: {
            mixpanel: "chatbot_feedback_submitted",
        },
    },
    client_manager_property_select: {
        description:
            "Fires when a user selects a saved property from the Properties dropdown inside a report tool.",
        mappings: {
            mixpanel: "client_manager_property_select",
        },
    },
    client_manager_loan_select: {
        description:
            "Fires when a user selects a saved loan from the Loan Scenarios dropdown inside a report tool.",
        mappings: {
            mixpanel: "client_manager_loan_select",
        },
    },
    saved_reports_select: {
        description: "Fires when a user opens a previously saved report from the Saved Reports dropdown.",
        mappings: {
            mixpanel: "saved_reports_select",
        },
    },

    // Navigation events
    nav_rail_item_click: {
        description: "Fires when a user clicks any navigation item in the left rail.",
        mappings: {
            mixpanel: "nav_rail_item_click",
        },
    },
    nav_search_query: {
        description: "Fires when a user enters a search query and results are displayed.",
        mappings: {
            mixpanel: "nav_search_query",
        },
    },
    nav_search_dropdown_click: {
        description: "Fires when a user clicks favorites or recents.",
        mappings: {
            mixpanel: "nav_search_dropdown_click",
        },
    },
    nav_search_result_click: {
        description: "Fires when a user clicks a search result.",
        mappings: {
            mixpanel: "nav_search_result_click",
        },
    },
    nav_product_switcher_click: {
        description: "Fires when a user selects a product from the switcher.",
        mappings: {
            mixpanel: "nav_product_switcher_click",
        },
    },
    nav_user_menu_item_click: {
        description: "Fires when a user clicks an item in the user profile menu.",
        mappings: {
            mixpanel: "nav_user_menu_item_click",
        },
    },
    nav_search_dropdown_view_all_click: {
        description: 'Fires when a user clicks "View all" in the Favorites section of the search dropdown.',
        mappings: {
            mixpanel: "nav_search_dropdown_view_all_click",
        },
    },
    // Week on Highway events
    social_share_click: {
        description: "Fires when a user clicks a Social Share card or 'View all'",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_social_share_click`,
        },
        validTools: ["woh"],
    },
    social_studio_click: {
        description: "Fires when a user clicks a Social Studio script card or 'View all'",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_social_studio_click`,
        },
        validTools: ["woh"],
    },
    newsletter_click: {
        description: "Fires when a user clicks the Newsletter 'Read more'",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_newsletter_click`,
        },
        validTools: ["woh"],
    },
    presentation_expressway_click: {
        description: "Fires when a user clicks the Presentation Expressway card",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_presentation_expressway_click`,
        },
        validTools: ["woh"],
    },
    blog_click: {
        description: "Fires when a user User clicks the Blog 'Read more'",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_blog_click`,
        },
        validTools: ["woh"],
    },
    more_this_week_click: {
        description: "Fires when a user clicks the 'More This Week' widget CTA",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_more_this_week_click`,
        },
        validTools: ["woh"],
    },
    webinars_click: {
        description: "Fires when a user clicks a webinar's Register / card",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_webinars_click`,
        },
        validTools: ["woh"],
    },
    page_exit: {
        description: "Fires when a user navigates away from Week on the Highway",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_page_exit`,
        },
        validTools: ["woh"],
    },
    view_all_updates_click: {
        description: "Fires when a user clicks 'Watch all of this week's updates'",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_view_all_updates_click`,
        },
        validTools: ["woh"],
    },
};
